import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useBookingSources } from 'hooks/api/useBookingSources';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import OccupancyStrip from './OccupancyStrip';
import DayViewPanel from './DayViewPanel';
import OccupancyCalendar from './OccupancyCalendar';
import { parseDayKey } from 'utils/calendarDate';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useBookings } from 'hooks/api/useBooking';
import { bookingService } from 'api/services/bookingService';
import { propertyService } from 'api/services/propertyService';
import { agentService } from 'api/services/agentService';
import { corporateService } from 'api/services/corporateService';
import { otaService } from 'api/services/otaService';
import type { Booking, BookingSearchRequest } from 'api/types/booking.types';
import type { SelectOption } from 'components/ui/SyutSelect';
import BookingViewModal from 'components/modals/modules/booking-module/BookingViewModal';
import BookingFormModal from 'components/modals/modules/booking-module/BookingFormModal';
import ExtendBlockModal from 'components/modals/modules/booking-module/ExtendBlockModal';
import BlockBookingModal from 'components/modals/modules/booking-module/BlockBookingModal';
import BookingPdfPreviewModal from 'components/modals/modules/booking-module/BookingPdfPreviewModal';
import ChangeSourceModal from 'components/modals/modules/booking-module/ChangeSourceModal';
import ChangeSettlementModal from 'components/modals/modules/booking-module/ChangeSettlementModal';
import { SETTLEMENT_MODE_OPTIONS, settlementModeLabel, type SettlementMode } from 'api/types/settlement.types';
import { todayKey } from 'utils/calendarDate';
import QuickCheckInModal from 'components/modals/modules/booking-module/QuickCheckInModal';
import CheckOutRoomPickerModal from 'components/modals/modules/frontoffice-module/CheckOutRoomPickerModal';
import MarkNoShowConfirmDialog from 'components/modals/modules/booking-module/MarkNoShowConfirmDialog';
import CancelBookingConfirmDialog from 'components/modals/modules/booking-module/CancelBookingConfirmDialog';
import ExtendStayModal from 'components/modals/modules/booking-module/ExtendStayModal';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import IssueInvoicesModal, { invoiceActions } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import { formatDate, formatCurrency, getStatusBadgeClass } from 'components/modals/modules/booking-module/booking.utils';

/** The three worksheet views, switched from the ModuleHeader toggle. */
type ViewMode = 'all' | 'day' | 'calendar';

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const bookingStatusOptions: SelectOption[] = [
  { value: 'TENTATIVE', label: 'Tentative' },
  { value: 'BLOCKED', label: 'Blocked' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'CHECKED_IN', label: 'Checked In' },
  { value: 'CHECKED_OUT', label: 'Checked Out' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'NO_SHOW', label: 'No Show' },
];


const dateTypeOptions: SelectOption[] = [
  { value: 'checkin', label: 'Check-In Date' },
  { value: 'checkout', label: 'Check-Out Date' },
  { value: 'created', label: 'Created Date' },
  { value: 'advance_due', label: 'Advance Due Date' },
  { value: 'block_expiry', label: 'Block Expiry' },
];

const advanceStatusOptions: SelectOption[] = [
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partial' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
];

// ============================================================================
// HELPERS
// ============================================================================

/** Returns the most-actionable identity for a booking based on its source. */
const resolveBookedBy = (b: Booking): { primary: string; secondary?: string; kind: 'guest' | 'agent' | 'corporate' | 'ota' } => {
  switch ((b.bookingSource || '').toLowerCase()) {
    case 'travel agent':
      if (b.agentName) return { primary: b.agentName, secondary: b.agentCode, kind: 'agent' };
      break;
    case 'ota':
      if (b.otaDisplayName || b.otaName) return { primary: b.otaDisplayName || b.otaName!, secondary: b.otaCode, kind: 'ota' };
      break;
    case 'corporate':
      if (b.corporateName) return { primary: b.corporateName, secondary: b.corporateCode, kind: 'corporate' };
      break;
  }
  // Fallback: guest (Direct / Walk-In, or any source missing the party).
  return { primary: b.guestFullName?.trim() || '—', secondary: b.guestPhone || undefined, kind: 'guest' };
};

/** Reference number that the booking party uses to refer to the booking. */
const resolveReference = (b: Booking): string => {
  switch ((b.bookingSource || '').toLowerCase()) {
    case 'ota':           return b.otaBookingRef || '—';
    case 'travel agent':  return b.agentCode || '—';
    case 'corporate':     return b.corporateCode || '—';
    default:              return '—';
  }
};

/** Bucket: 'paid' | 'partial' | 'unpaid' | 'overdue' | 'na' (no advance required). */
const computeAdvanceBucket = (b: Booking): 'paid' | 'partial' | 'unpaid' | 'overdue' | 'na' => {
  const required = b.advanceRequired ?? 0;
  const received = b.advanceReceived ?? 0;
  if (required <= 0) return 'na';
  if (received >= required) return 'paid';
  // Past due?
  if (b.advanceDueDate) {
    const due = new Date(b.advanceDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    if (due < today) return 'overdue';
  }
  return received > 0 ? 'partial' : 'unpaid';
};

const advanceBucketStyle = (bucket: ReturnType<typeof computeAdvanceBucket>): { label: string; bg: string; color: string } => {
  switch (bucket) {
    case 'paid':    return { label: 'Paid',    bg: '#dcfce7', color: '#166534' };
    case 'partial': return { label: 'Partial', bg: '#fef3c7', color: '#92400e' };
    case 'unpaid':  return { label: 'Unpaid',  bg: '#e5e7eb', color: '#374151' };
    case 'overdue': return { label: 'Overdue', bg: '#fee2e2', color: '#991b1b' };
    case 'na':      return { label: '—',       bg: 'transparent', color: 'var(--text-muted)' };
  }
};

/** Days remaining label for a date string (positive=future, negative=past). */
const daysUntil = (dateStr?: string): { label: string; color: string } | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return { label: 'Today', color: '#92400e' };
  if (diff < 0)   return { label: `Overdue ${Math.abs(diff)}d`, color: '#991b1b' };
  if (diff === 1) return { label: 'Tomorrow', color: '#92400e' };
  if (diff <= 3)  return { label: `In ${diff}d`, color: '#92400e' };
  return { label: `In ${diff}d`, color: 'var(--text-muted)' };
};

/** Hours remaining label for a timestamp string. */
const hoursUntil = (tsStr?: string): { label: string; color: string } | null => {
  if (!tsStr) return null;
  const t = new Date(tsStr).getTime();
  const now = Date.now();
  const diffMs = t - now;
  const diffHrs = Math.round(diffMs / 3600000);
  if (diffMs <= 0) return { label: 'Expired', color: '#991b1b' };
  if (diffHrs < 1) return { label: '< 1h', color: '#991b1b' };
  if (diffHrs < 4) return { label: `${diffHrs}h`, color: '#991b1b' };
  if (diffHrs < 24) return { label: `${diffHrs}h`, color: '#92400e' };
  const days = Math.round(diffHrs / 24);
  return { label: `${days}d`, color: 'var(--text-muted)' };
};

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select',            label: '',            field: 'select',            required: true,  defaultVisible: true,  width: 50,  sortable: false },
  { id: 'bookingNumber',     label: 'Booking #',   field: 'bookingNumber',     required: false, defaultVisible: true,                sortable: true },
  { id: 'bookingSource',     label: 'Source',      field: 'bookingSource',     required: false, defaultVisible: true,                sortable: true },
  { id: 'bookedBy',          label: 'Booked By',   field: 'bookedBy',          required: false, defaultVisible: true,                sortable: false },
  { id: 'reference',         label: 'Reference',   field: 'reference',         required: false, defaultVisible: true,                sortable: false },
  { id: 'netTotal',          label: 'Net Total',   field: 'netTotal',          required: false, defaultVisible: true,                sortable: true },
  { id: 'advance',           label: 'Advance',     field: 'advance',           required: false, defaultVisible: true,                sortable: false },
  { id: 'dueExpires',        label: 'Due / Expires', field: 'dueExpires',      required: false, defaultVisible: true,                sortable: false },
  { id: 'bookingStatusCode', label: 'Status',      field: 'bookingStatusCode', required: false, defaultVisible: true,                sortable: true },
  // Stay/property block — visible by default, but moved to the end of the visible group
  // so the operational signal columns (advance / due / status) sit closer to identity.
  { id: 'propertyName',      label: 'Property',    field: 'propertyName',      required: false, defaultVisible: true,                sortable: true },
  { id: 'checkInDate',       label: 'Check-In',    field: 'checkInDate',       required: false, defaultVisible: true,                sortable: true },
  { id: 'checkOutDate',      label: 'Check-Out',   field: 'checkOutDate',      required: false, defaultVisible: true,                sortable: true },
  { id: 'nightsRooms',       label: 'Nights · Rms',field: 'nightsRooms',       required: false, defaultVisible: true,                sortable: false },
  // Hidden by default — surfaceable via Column Selector
  { id: 'guestFullName',     label: 'Guest Name',  field: 'guestFullName',     required: false, defaultVisible: false,               sortable: true },
  { id: 'guestPhone',        label: 'Guest Phone', field: 'guestPhone',        required: false, defaultVisible: false,               sortable: false },
  { id: 'agentName',         label: 'Agent',       field: 'agentName',         required: false, defaultVisible: false,               sortable: true },
  { id: 'corporateName',     label: 'Corporate',   field: 'corporateName',     required: false, defaultVisible: false,               sortable: true },
  { id: 'otaDisplayName',    label: 'OTA',         field: 'otaDisplayName',    required: false, defaultVisible: false,               sortable: true },
  { id: 'settlementMode',    label: 'Settlement',  field: 'settlementMode',    required: false, defaultVisible: false,               sortable: false },
  { id: 'grandTotal',        label: 'Gross Total', field: 'grandTotal',        required: false, defaultVisible: false,               sortable: true },
  { id: 'balanceDue',        label: 'Balance',     field: 'balanceDue',        required: false, defaultVisible: false,               sortable: false },
  // Audit columns (hidden by default)
  { id: 'createdByName',     label: 'Taken By',      field: 'createdByName',   required: false, defaultVisible: false,               sortable: false },
  { id: 'createdAt',         label: 'Created Date', field: 'createdAt',        required: false, defaultVisible: false,               sortable: true },
  { id: 'approvedByName',    label: 'Approved By', field: 'approvedByName',    required: false, defaultVisible: false,               sortable: false },
  { id: 'approvedAt',        label: 'Approved Date', field: 'approvedAt',      required: false, defaultVisible: false,               sortable: false },
  // TXN-STAT — hidden by default, surfaceable via Column Selector
  { id: 'actualCheckInAt',         label: 'Checked-In At',  field: 'actualCheckInAt',         required: false, defaultVisible: false,  sortable: true },
  { id: 'actualCheckOutAt',        label: 'Checked-Out At', field: 'actualCheckOutAt',        required: false, defaultVisible: false,  sortable: true },
  { id: 'checkedInRoomCount',      label: 'Rooms Assigned', field: 'checkedInRoomCount',      required: false, defaultVisible: false,  sortable: false },
  { id: 'totalCollectedAtCheckout',label: 'Collected at C/O', field: 'totalCollectedAtCheckout', required: false, defaultVisible: false, sortable: false },
  { id: 'actions',           label: 'Actions',     field: 'actions',           required: true,  defaultVisible: true,  width: 80,    sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT (unchanged from original)
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0, width: '320px', maxHeight: '500px',
          background: 'var(--bg-primary)', border: '2px solid var(--primary)', borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)', zIndex: 1000, animation: 'slideDown 0.2s ease',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem', borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Manage Columns</h3>
            <button onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}>
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div key={column.id} onClick={() => toggleColumn(column.id)}
                style={{ display: 'flex', alignItems: 'center', padding: '0.625rem 1.25rem', cursor: column.required ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', gap: '0.75rem', opacity: column.required ? 0.6 : 1 }}>
                <div style={{
                  width: '1.125rem', height: '1.125rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--border-color)', borderRadius: '0.25rem', transition: 'all 0.2s ease', flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{ padding: '1rem', borderTop: '2px solid var(--border-color)', background: 'var(--bg-primary)', boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', gap: '0.75rem', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmResetOpen} title="Reset Columns" message="Reset to default column configuration?"
        confirmLabel="Reset" cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)} />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface BookingListScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const BookingListScreen: React.FC<BookingListScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<BookingSearchRequest>({ page: 1, pageSize: 25, showCancelled: false });
  const { data, loading: gridLoading, pagination, refetch: refetchGrid } = useBookings(queryParams);

  /**
   * One refresh routine for the whole screen. Every row action already calls
   * refetch(); wrapping it here means the occupancy strip refreshes with the
   * grid instead of each call site having to remember. Cancelling a booking
   * must move free rooms 6 -> 7 on the strip, not merely drop a grid row --
   * a stale HUD is worse than none, because the agent quotes from it.
   * Design: CRS-Booking-Worksheet-Day-View-Design.md section 8.2.
   */
  const refetch = useCallback(() => {
    refetchGrid();
    setDayRefreshKey((k) => k + 1);
    window.dispatchEvent(new Event('crs:bookings-changed'));
  }, [refetchGrid]);

  // --- View state ---
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterProperty, setFilterProperty] = useState('');

  /**
   * Single-property tenants get the property pre-selected. Without it the strip,
   * Day View and Calendar all open on a "select a property" dead end that reads
   * as an empty screen, and the agent has to open the filter panel and Apply
   * before any of them show anything.
   *
   * Only when there is EXACTLY one active property — with several, picking one
   * silently would scope the screen to a property nobody chose.
   */
  const { defaultPropertyId, isSingleProperty } = useDefaultProperty();
  const seededPropertyRef = useRef(false);

  useEffect(() => {
    // Seed once, and never over an explicit choice the user has already made.
    if (seededPropertyRef.current || !defaultPropertyId || filterProperty) return;
    seededPropertyRef.current = true;
    setFilterProperty(defaultPropertyId);
    // Push it into the query too: the filter panel only applies on Apply, and
    // the point is that the agent never has to open the panel at all.
    setQueryParams((prev) => ({ ...prev, propertyId: defaultPropertyId, page: 1 }));
  }, [defaultPropertyId, filterProperty]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  // Source list is enum-driven (common.tbl_enum_master), not a hardcoded array.
  const { options: bookingSourceOptions, loading: sourcesLoading } = useBookingSources();
  const [filterSource, setFilterSource] = useState('');
  /**
   * The day highlighted in the occupancy strip. Selecting a day narrows the
   * grid to that day's ARRIVALS and says so with a removable chip — never a
   * silent filter rewrite. Labelled 'Arrivals', not 'Stay date', because the
   * grid filters on check-in; the full stay-overlap list is the Day View tab
   * (design §6), which is not built yet.
   */
  const [stripDate, setStripDate] = useState('');

  /**
   * ONE reusable Day View tab, re-scoped by strip clicks - never one tab per
   * date. Day-to-day comparison is the core loop; accumulating tabs would make
   * the agent a tab janitor. Always present, defaulting to today, because an
   * agent wants today's position on landing and a tab that materialises on
   * first click is undiscoverable.
   */
  const [activeTab, setActiveTab] = useState<ViewMode>('all');
  const [dayViewDate, setDayViewDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  /** Bumped by the shared refetch so the Day View reloads with the grid. */
  const [dayRefreshKey, setDayRefreshKey] = useState(0);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterCorporate, setFilterCorporate] = useState('');
  const [filterOta, setFilterOta] = useState('');
  const [filterAdvanceStatus, setFilterAdvanceStatus] = useState('');
  // Settlement review (PMS slice 3b): upcoming arrivals only — check-in on or
  // after a date (today by default), so stale never-checked-in rows stay out.
  const [filterSettlements, setFilterSettlements] = useState<string[]>([]);
  const [filterSettlementFrom, setFilterSettlementFrom] = useState(() => todayKey());
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDateType, setFilterDateType] = useState('checkin');
  const [filterApprovalPending, setFilterApprovalPending] = useState(false);
  const [filterShowCancelled, setFilterShowCancelled] = useState(false);

  // --- Active quick-chip (for highlighting). Null when none active. ---
  const [activeChip, setActiveChip] = useState<string | null>(null);

  // --- Modal state ---
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Booking | null>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [formRecord, setFormRecord] = useState<Booking | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const createInstanceCounter = useRef(0);
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Advance payment capture modal ---
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [advanceBooking, setAdvanceBooking] = useState<Booking | null>(null);

  // --- Block / Extension modals ---
  // Block (create) → BlockBookingModal (config-driven default + cap, optional remarks).
  // The old inline free-form datetime modal was retired alongside Edit Block Expiry.
  const [blockTarget, setBlockTarget] = useState<Booking | null>(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);

  // One-click "Extend Block" replaces the old free-form datetime editor for
  // the BLOCKED-state action menu. The free-form modal above is now reachable
  // only via the Block (create) flow; managers who need ad-hoc adjustments
  // can still cancel + re-block.
  const [extendBlockOpen, setExtendBlockOpen] = useState(false);
  const [extendBlockTarget, setExtendBlockTarget] = useState<Booking | null>(null);

  const [confirmBlockedOpen, setConfirmBlockedOpen] = useState(false);
  const [confirmBlockedTarget, setConfirmBlockedTarget] = useState<Booking | null>(null);
  const [confirmBlockedSaving, setConfirmBlockedSaving] = useState(false);

  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [extensionTarget, setExtensionTarget] = useState<Booking | null>(null);
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionRemarks, setExtensionRemarks] = useState('');
  const [extensionSaving, setExtensionSaving] = useState(false);

  // --- TXN-STAT Quick Check-In / Check-Out / No-Show modals ---
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkInTarget, setCheckInTarget] = useState<Booking | null>(null);
  /** Post-check-in amend session (QuickCheckInModal mode="amend"). */
  const [amendTarget, setAmendTarget] = useState<Booking | null>(null);
  /** Surgical source/channel correction session. */
  const [sourceTarget, setSourceTarget] = useState<Booking | null>(null);
  // In-house "Change settlement" (PMS slice 3b).
  const [settlementTarget, setSettlementTarget] = useState<Booking | null>(null);
  /** Settle / Invoice a checked-out stay whose account is still open (2026-10-16). */
  const [invoiceTarget, setInvoiceTarget] = useState<string | null>(null);
  const [checkOutModalOpen, setCheckOutModalOpen] = useState(false);
  const [checkOutTarget, setCheckOutTarget] = useState<Booking | null>(null);
  const [noShowModalOpen, setNoShowModalOpen] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState<Booking | null>(null);
  // Cancel Booking (replaces the legacy Delete row-action). Available for
  // TENTATIVE / BLOCKED / PENDING_APPROVAL / CONFIRMED — i.e. before the
  // guest is in-house and before any terminal state.
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);
  // Extend Stay (CHECKED_IN only) — pushes the check-out date forward,
  // consumes inventory for the new nights, optionally collects payment.
  const [extendStayModalOpen, setExtendStayModalOpen] = useState(false);
  const [extendStayTarget, setExtendStayTarget] = useState<Booking | null>(null);

  const openCheckInModal = useCallback((booking: Booking) => {
    // If this booking's check-in is parked in the minimized strip, restore
    // that session instead of opening a fresh (empty) one.
    const modalId = tabId ? `${tabId}-checkin-modal-${booking.bookingNumber}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setCheckInTarget(booking);
    setCheckInModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized]);
  const openAmendModal = useCallback((booking: Booking) => {
    // Same restore-first contract, distinct modal id for the amend session.
    const modalId = tabId ? `${tabId}-amend-modal-${booking.bookingNumber}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setAmendTarget(booking);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized]);
  const openCheckOutModal = useCallback((booking: Booking) => {
    setCheckOutTarget(booking);
    setCheckOutModalOpen(true);
  }, []);
  const openNoShowModal = useCallback((booking: Booking) => {
    setNoShowTarget(booking);
    setNoShowModalOpen(true);
  }, []);
  const openCancelModal = useCallback((booking: Booking) => {
    setCancelTarget(booking);
    setCancelModalOpen(true);
  }, []);
  const openExtendStayModal = useCallback((booking: Booking) => {
    setExtendStayTarget(booking);
    setExtendStayModalOpen(true);
  }, []);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('booking-list-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('booking-list-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('booking-list-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- Search debounce ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Async dropdown loaders ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);
  const loadAgentOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await agentService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 200, includeInactive: false });
    return res.data.map(a => ({ value: a.id, label: `${a.agentCode} - ${a.agentName}` }));
  }, []);
  const loadCorporateOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await corporateService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 200, includeInactive: false });
    return res.data.map(c => ({ value: c.id, label: `${c.corporateCode} - ${c.companyName}` }));
  }, []);
  const loadOtaOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await otaService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 200, includeInactive: false });
    return res.data.map(o => ({ value: o.id, label: `${o.otaCode} - ${o.otaName}` }));
  }, []);

  // --- View handler ---
  const handleView = useCallback((booking: Booking) => {
    const modalId = `${tabId}-view-modal-${booking.bookingId}`;
    if (tabId && isMinimized(modalId)) {
      dismissMinimized(modalId);
      setViewRecord(booking);
      setViewModalOpen(true);
      return;
    }
    setViewRecord(booking);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized]);

  const handleRestoreView = useCallback((bookingId: string) => {
    const rec = data.find(r => r.bookingId === bookingId);
    if (rec) {
      setViewRecord(rec);
      setViewModalOpen(true);
    }
  }, [data]);

  // --- Edit handler ---
  const handleEdit = useCallback((booking: Booking) => {
    const modalId = `${tabId}-form-modal-edit-${booking.bookingId}`;
    if (tabId && isMinimized(modalId)) {
      dismissMinimized(modalId);
      setFormRecord(booking);
      setFormMode('edit');
      setFormModalOpen(true);
      return;
    }
    if (formModalOpen && formModalMinimizeRef.current) {
      formModalMinimizeRef.current();
    }
    setFormRecord(booking);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    if (formModalOpen && formModalMinimizeRef.current) {
      formModalMinimizeRef.current();
    }
    createInstanceCounter.current += 1;
    const newId = String(createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Form save success ---
  const handleFormSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setFormRecord(null);
    refetch();
  }, [refetch]);

  // --- Form restore handler ---
  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
    if (mode === 'edit' && recordCode) {
      const rec = data.find(r => r.bookingNumber === recordCode);
      if (rec) setFormRecord(rec);
    }
    if (mode === 'create' && instanceId) {
      setCreateInstanceId(instanceId);
    }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [data]);

  // "Download Confirmation" now opens an in-app preview modal — the cashier
  // can review the PDF first and then download it. The Download button on
  // the modal triggers the actual file save.
  const [pdfPreviewBooking, setPdfPreviewBooking] = useState<Booking | null>(null);
  const handleDownloadPdf = useCallback((booking: Booking) => {
    setPdfPreviewBooking(booking);
  }, []);

  // --- Block / Extension handlers ---
  // Opens BlockBookingModal — the modal owns the policy lookup, default
  // expiry, cap enforcement, and submit.
  const openBlockModal = useCallback((booking: Booking) => {
    setBlockTarget(booking);
    setBlockModalOpen(true);
  }, []);

  const openExtendBlockModal = useCallback((booking: Booking) => {
    setExtendBlockTarget(booking);
    setExtendBlockOpen(true);
  }, []);

  const handleConfirmBlockedClick = useCallback((booking: Booking) => {
    setConfirmBlockedTarget(booking);
    setConfirmBlockedOpen(true);
  }, []);

  const handleConfirmBlockedExecute = useCallback(async () => {
    if (!confirmBlockedTarget) return;
    setConfirmBlockedSaving(true);
    try {
      await bookingService.confirmBlocked(confirmBlockedTarget.bookingId);
      notificationService.toast.success(`Booking "${confirmBlockedTarget.bookingNumber}" confirmed.`);
      setConfirmBlockedOpen(false);
      setConfirmBlockedTarget(null);
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to confirm booking.';
      notificationService.toast.error(msg);
    } finally {
      setConfirmBlockedSaving(false);
    }
  }, [confirmBlockedTarget, refetch]);

  const openExtensionModal = useCallback((booking: Booking) => {
    setExtensionTarget(booking);
    // Default proposed date = current advance_due_date + 3 days, clamped to check-in.
    const base = booking.advanceDueDate ? new Date(booking.advanceDueDate) : new Date();
    base.setDate(base.getDate() + 3);
    const checkIn = new Date(booking.checkInDate);
    if (base > checkIn) base.setTime(checkIn.getTime());
    setExtensionDate(base.toISOString().slice(0, 10));
    setExtensionRemarks('');
    setExtensionModalOpen(true);
  }, []);

  const handleExtensionConfirm = useCallback(async () => {
    if (!extensionTarget) return;
    if (!extensionRemarks.trim()) {
      notificationService.toast.error('Please provide a justification for the extension.');
      return;
    }
    setExtensionSaving(true);
    try {
      await bookingService.requestDueDateExtension(extensionTarget.bookingId, extensionDate, extensionRemarks.trim());
      notificationService.toast.success('Extension requested. Booking moved to Pending Approval.');
      setExtensionModalOpen(false);
      setExtensionTarget(null);
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to request extension.';
      notificationService.toast.error(msg);
    } finally {
      setExtensionSaving(false);
    }
  }, [extensionTarget, extensionDate, extensionRemarks, refetch]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<Booking>[] = useMemo(() => [
    { id: 'bookingNumber', header: 'Booking #', accessorKey: 'bookingNumber', size: 160, enableSorting: true,
      cell: ({ row, getValue }) => {
        // Leading urgency dot: red = overdue tentative, amber = blocked expiring soon, yellow = pending approval.
        const b = row.original;
        let dot: { color: string; title: string } | null = null;
        if (b.bookingStatusCode === 'PENDING_APPROVAL') {
          dot = { color: '#eab308', title: 'Pending approval' };
        } else if (b.bookingStatusCode === 'TENTATIVE' && b.advanceDueDate) {
          const due = new Date(b.advanceDueDate); due.setHours(0, 0, 0, 0);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          if (due < today) dot = { color: '#dc2626', title: 'Advance overdue' };
        } else if (b.bookingStatusCode === 'BLOCKED' && b.expiresAt) {
          const ms = new Date(b.expiresAt).getTime() - Date.now();
          if (ms <= 2 * 3600 * 1000) dot = { color: '#f59e0b', title: 'Block expiring soon' };
        }
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, color: 'var(--primary)', fontSize: '0.85rem' }}>
            {dot && (
              <span title={dot.title} style={{ width: 8, height: 8, borderRadius: '50%', background: dot.color, flexShrink: 0 }} />
            )}
            {getValue() as string}
          </span>
        );
      },
    },
    { id: 'bookingSource', header: 'Source', accessorKey: 'bookingSource', size: 110, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.8rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'bookedBy', header: 'Booked By', accessorKey: 'guestFullName', size: 200,
      cell: ({ row }) => {
        const r = resolveBookedBy(row.original);
        const kindIcon = {
          guest:     <IconSystem.user size={12} />,
          agent:     <IconSystem.tag size={12} />,
          corporate: <IconSystem.home size={12} />,
          ota:       <IconSystem.link size={12} />,
        }[r.kind];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{kindIcon}</span>
              <span style={{ fontWeight: 500 }}>{r.primary}</span>
              {row.original.guestIsVip && r.kind === 'guest' && (
                <span style={{ marginLeft: '0.25rem', fontSize: '0.6rem', padding: '0.0625rem 0.25rem', borderRadius: '3px', background: '#dcfce7', color: '#166534', fontWeight: 600 }}>VIP</span>
              )}
            </span>
            {r.secondary && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{r.secondary}</span>
            )}
          </div>
        );
      },
    },
    { id: 'reference', header: 'Reference', accessorKey: 'otaBookingRef', size: 130,
      cell: ({ row }) => <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{resolveReference(row.original)}</span>,
    },
    { id: 'netTotal', header: 'Net Total', accessorKey: 'finalGrandTotal', size: 110, enableSorting: true,
      // Net Total represents the actual amount the guest owes — that's
      // finalGrandTotal (netAmount + addonTotal) for bookings created after the
      // add-on migration. Legacy bookings without final_grand_total fall back to
      // netAmount (sum of room/meal/extra minus discount).
      cell: ({ row }) => {
        const b = row.original as typeof row.original & { finalGrandTotal?: number; addonTotal?: number };
        const v = b.finalGrandTotal ?? b.netAmount ?? b.grandTotal ?? 0;
        const hasAddons = (b.addonTotal ?? 0) > 0;
        return (
          <span
            style={{ fontSize: '0.85rem', fontWeight: 500, textAlign: 'right', display: 'block' }}
            title={hasAddons ? `Includes add-ons: ${formatCurrency(b.addonTotal ?? 0)}` : undefined}
          >
            {formatCurrency(v)}
          </span>
        );
      },
    },
    { id: 'advance', header: 'Advance', accessorKey: 'advanceReceived', size: 165,
      cell: ({ row }) => {
        const b = row.original;
        const required = b.advanceRequired ?? 0;
        const received = b.advanceReceived ?? 0;
        const bucket = computeAdvanceBucket(b);
        const sty = advanceBucketStyle(bucket);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', lineHeight: 1.2 }}>
            <span style={{ fontSize: '0.8rem', textAlign: 'right' }}>
              {formatCurrency(received)} <span style={{ color: 'var(--text-muted)' }}>/ {formatCurrency(required)}</span>
            </span>
            <span style={{
              alignSelf: 'flex-end',
              fontSize: '0.65rem', fontWeight: 600,
              padding: '0.0625rem 0.375rem', borderRadius: '3px',
              background: sty.bg, color: sty.color,
            }}>{sty.label}</span>
          </div>
        );
      },
    },
    { id: 'dueExpires', header: 'Due / Expires', accessorKey: 'advanceDueDate', size: 130,
      cell: ({ row }) => {
        const b = row.original;
        if (b.bookingStatusCode === 'BLOCKED' && b.expiresAt) {
          const remaining = hoursUntil(b.expiresAt);
          const local = new Date(b.expiresAt);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontSize: '0.75rem' }}>{local.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              {remaining && <span style={{ fontSize: '0.65rem', fontWeight: 600, color: remaining.color }}>{remaining.label}</span>}
            </div>
          );
        }
        if (b.bookingStatusCode === 'TENTATIVE' && b.advanceDueDate) {
          const remaining = daysUntil(b.advanceDueDate);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontSize: '0.8rem' }}>{formatDate(b.advanceDueDate)}</span>
              {remaining && <span style={{ fontSize: '0.65rem', fontWeight: 600, color: remaining.color }}>{remaining.label}</span>}
            </div>
          );
        }
        return <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>—</span>;
      },
    },
    { id: 'bookingStatusCode', header: 'Status', accessorKey: 'bookingStatusCode', size: 130, enableSorting: true,
      cell: ({ row }) => {
        const b = row.original;
        return <span className={`badge ${getStatusBadgeClass(b.bookingStatusCode)}`}>{b.bookingStatus || b.bookingStatusCode}</span>;
      },
    },
    // Stay/property block — moved to the end of the visible group so the operational
    // signal columns (advance / due / status) sit closer to the booking identity.
    { id: 'propertyName', header: 'Property', accessorKey: 'propertyName', size: 140, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'checkInDate', header: 'Check-In', accessorKey: 'checkInDate', size: 105, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.8rem' }}>{formatDate(getValue() as string)}</span>,
    },
    { id: 'checkOutDate', header: 'Check-Out', accessorKey: 'checkOutDate', size: 105, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.8rem' }}>{formatDate(getValue() as string)}</span>,
    },
    { id: 'nightsRooms', header: 'Nights · Rms', accessorKey: 'numNights', size: 100,
      cell: ({ row }) => (
        <span style={{ fontSize: '0.8rem', textAlign: 'center', display: 'block' }}>
          {row.original.numNights}n · {row.original.totalRooms}r
        </span>
      ),
    },
    // Hidden by default — surfaceable via Column Selector
    { id: 'guestFullName', header: 'Guest Name', accessorKey: 'guestFullName', size: 170, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'guestPhone', header: 'Guest Phone', accessorKey: 'guestPhone', size: 120,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'agentName', header: 'Agent', accessorKey: 'agentName', size: 150, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'corporateName', header: 'Corporate', accessorKey: 'corporateName', size: 150, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'otaDisplayName', header: 'OTA', accessorKey: 'otaDisplayName', size: 130, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    // Who pays (PMS slice 3b) — hidden by default.
    { id: 'settlementMode', header: 'Settlement', accessorKey: 'settlementMode', size: 150, enableSorting: false,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{settlementModeLabel((getValue() as string) || 'GUEST')}</span>,
    },
    { id: 'grandTotal', header: 'Gross Total', accessorKey: 'grandTotal', size: 110, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem', textAlign: 'right', display: 'block' }}>{formatCurrency(getValue() as number)}</span>,
    },
    { id: 'balanceDue', header: 'Balance', accessorKey: 'balanceDue', size: 110,
      cell: ({ getValue }) => {
        const v = getValue() as number;
        return <span style={{ fontSize: '0.85rem', fontWeight: 500, textAlign: 'right', display: 'block', color: v > 0 ? 'var(--danger, #dc2626)' : 'var(--text-primary)' }}>{formatCurrency(v)}</span>;
      },
    },
    // Audit columns (hidden by default)
    { id: 'createdByName', header: 'Taken By', accessorKey: 'createdByName', size: 150,
      cell: ({ getValue }) => <span style={{ fontSize: '0.8rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'createdAt', header: 'Created Date', accessorKey: 'createdAt', size: 130, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        if (!v) return <span style={{ fontSize: '0.8rem' }}>-</span>;
        const d = new Date(v);
        return <span style={{ fontSize: '0.75rem' }}>{d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
      },
    },
    { id: 'approvedByName', header: 'Approved By', accessorKey: 'approvedByName', size: 150,
      cell: ({ getValue }) => <span style={{ fontSize: '0.8rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'approvedAt', header: 'Approved Date', accessorKey: 'approvedAt', size: 130,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        if (!v) return <span style={{ fontSize: '0.8rem' }}>-</span>;
        const d = new Date(v);
        return <span style={{ fontSize: '0.75rem' }}>{d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
      },
    },
    { id: 'actualCheckInAt', header: 'Checked-In At', accessorKey: 'actualCheckInAt', size: 130, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        if (!v) return <span style={{ fontSize: '0.8rem' }}>-</span>;
        const d = new Date(v);
        return <span style={{ fontSize: '0.75rem' }}>{d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
      },
    },
    { id: 'actualCheckOutAt', header: 'Checked-Out At', accessorKey: 'actualCheckOutAt', size: 130, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        if (!v) return <span style={{ fontSize: '0.8rem' }}>-</span>;
        const d = new Date(v);
        return <span style={{ fontSize: '0.75rem' }}>{d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
      },
    },
    { id: 'checkedInRoomCount', header: 'Rooms Assigned', accessorKey: 'checkedInRoomCount', size: 110,
      cell: ({ getValue }) => {
        const v = getValue() as number | undefined;
        return <span style={{ fontSize: '0.8rem' }}>{v != null ? v : '-'}</span>;
      },
    },
    { id: 'totalCollectedAtCheckout', header: 'Collected at C/O', accessorKey: 'totalCollectedAtCheckout', size: 130,
      cell: ({ getValue }) => {
        const v = getValue() as number | undefined;
        return <span style={{ fontSize: '0.8rem' }}>{v != null ? formatCurrency(v) : '-'}</span>;
      },
    },
  ], []);

  // --- Advance payment capture ---
  const handleRecordAdvance = useCallback((booking: Booking) => {
    setAdvanceBooking(booking);
    setAdvanceModalOpen(true);
  }, []);

  const handleAdvanceSaved = useCallback(() => {
    setAdvanceModalOpen(false);
    setAdvanceBooking(null);
    refetch();
  }, [refetch]);

  // --- Row actions ---
  const getRowActions = useCallback((booking: Booking): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      actions.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(booking) });
    }
    const status = booking.bookingStatusCode;
    // Edit is allowed only while the booking is still mutable — i.e. before
    // the guest is in-house and before terminal lifecycle states.
    // CHECKED_IN, CHECKED_OUT, CANCELLED, NO_SHOW are excluded for the same
    // reasons documented on BookingViewModal.isEditable.
    const isEditable = !['CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(status);
    if (allowedActions.includes('edit') && isEditable) {
      actions.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(booking) });
    }
    const advReceived = booking.advanceReceived ?? 0;
    const advRequired = booking.advanceRequired ?? 0;
    const unpaidAdvance = advRequired > 0 && advReceived < advRequired;

    // Advance capture is meaningful only while the bill is still being built —
    // i.e. up to and including check-in. Once the booking is CHECKED_OUT the
    // bill was settled at the check-out step, so any further movement is a
    // refund/adjustment workflow (not yet implemented), not an advance.
    // CANCELLED / NO_SHOW are excluded for the obvious reason.
    if (
      allowedActions.includes('edit')
      && !['CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(status)
    ) {
      actions.push({ id: 'record-advance', label: 'Record Advance', icon: <IconSystem.dollarSign size={18} />, onClick: () => handleRecordAdvance(booking) });
    }
    // TENTATIVE-only lifecycle actions (mutates — gate on 'edit')
    if (allowedActions.includes('edit') && status === 'TENTATIVE') {
      actions.push({ id: 'block', label: 'Block', icon: <IconSystem.lock size={18} />, onClick: () => openBlockModal(booking) });
      if (unpaidAdvance) {
        actions.push({ id: 'request-extension', label: 'Request Due-Date Extension', icon: <IconSystem.calendar size={18} />, onClick: () => openExtensionModal(booking) });
      }
    }
    // TXN-STAT Quick Status Updates (Check-In / Check-Out / Mark No-Show)
    if (status === 'CONFIRMED' && allowedActions.includes('checkin')) {
      actions.push({ id: 'check-in', label: 'Check In', icon: <IconSystem.check size={18} />, onClick: () => openCheckInModal(booking) });
    }
    if (status === 'CONFIRMED' && allowedActions.includes('mark-no-show')) {
      actions.push({ id: 'mark-no-show', label: 'Mark as No-Show', icon: <IconSystem.warning size={18} />, onClick: () => openNoShowModal(booking) });
    }
    // Post-check-in amendment — GRC/guests/occupancy corrections while in-house.
    if (status === 'CHECKED_IN' && allowedActions.includes('checkin')) {
      actions.push({ id: 'edit-checkin', label: 'Edit Check-In', icon: <IconSystem.edit size={18} />, onClick: () => openAmendModal(booking) });
    }
    if (status === 'CHECKED_IN' && allowedActions.includes('checkout')) {
      actions.push({ id: 'check-out', label: 'Check Out', icon: <IconSystem.check size={18} />, onClick: () => openCheckOutModal(booking) });
    }
    // The rooms have left; the account is settled and invoiced here (2026-10-16).
    if (status === 'CHECKED_OUT' && invoiceActions().includes('view')) {
      actions.push({ id: 'settle-invoice', label: 'Settle / Invoice', icon: <IconSystem.file size={18} />, onClick: () => setInvoiceTarget(String(booking.bookingId)) });
    }
    if (status === 'CHECKED_IN' && allowedActions.includes('extend-stay')) {
      actions.push({ id: 'extend-stay', label: 'Extend Stay', icon: <IconSystem.calendar size={18} />, onClick: () => openExtendStayModal(booking) });
    }
    // Who pays (slice 3b): in house, or checked out until the lines are invoiced
    // (2026-10-16). Before check-in the booking form sets it. Own action — it decides invoices.
    if ((status === 'CHECKED_IN' || status === 'CHECKED_OUT') && allowedActions.includes('change-settlement')) {
      actions.push({ id: 'change-settlement', label: 'Change Settlement', icon: <IconSystem.dollarSign size={18} />, onClick: () => setSettlementTarget(booking) });
    }

    // BLOCKED-only lifecycle actions (mutates — gate on 'edit')
    if (allowedActions.includes('edit') && status === 'BLOCKED') {
      // One-click "Extend Block" (replaces "Edit Block Expiry"): opens a modal
      // that reads BLOCKING_POLICY config and extends by extension_hours,
      // capped at max_block_hold_hours. The modal's own guards hide the action
      // when ineligible, so we don't gate it here on policy — we'd need an
      // extra fetch per row.
      actions.push({ id: 'extend-block', label: 'Extend Block', icon: <IconSystem.clock size={18} />, onClick: () => openExtendBlockModal(booking) });
      actions.push({ id: 'confirm-blocked', label: 'Confirm Block', icon: <IconSystem.check size={18} />, onClick: () => handleConfirmBlockedClick(booking) });
    }
    // Attribution correction (wrong OTA/agent/corporate) — surgical, never
    // re-prices; allowed on every non-released status incl. CHECKED_OUT
    // (reconciliation-time fixes). Gate on 'edit'.
    if (allowedActions.includes('edit') && !['CANCELLED', 'NO_SHOW'].includes(status)) {
      actions.push({ id: 'change-source', label: 'Change Source / Channel', icon: <IconSystem.tag size={18} />, onClick: () => setSourceTarget(booking) });
    }
    // PDF download is an export-style action — gate on 'export'
    if (allowedActions.includes('export') && ['CONFIRMED', 'TENTATIVE', 'BLOCKED'].includes(status)) {
      actions.push({ id: 'download-pdf', label: 'Download Confirmation', icon: <IconSystem.download size={18} />, onClick: () => handleDownloadPdf(booking) });
    }
    // Cancel Booking (replaces legacy Delete). Available on every non-terminal,
    // non-in-house status. CHECKED_IN/CHECKED_OUT/CANCELLED/NO_SHOW are excluded
    // because at that point the guest is already in-house or the booking has
    // reached a terminal state — no inventory left to release via cancellation.
    const cancellable = ['TENTATIVE', 'BLOCKED', 'PENDING_APPROVAL', 'CONFIRMED'].includes(status);
    if (allowedActions.includes('cancel') && cancellable) {
      actions.push({ id: 'cancel-booking', label: 'Cancel Booking', icon: <IconSystem.x size={18} />, onClick: () => openCancelModal(booking), variant: 'danger' });
    }
    return actions;
  }, [allowedActions, handleView, handleEdit, handleRecordAdvance, openBlockModal, openExtendBlockModal, openExtensionModal, handleConfirmBlockedClick, handleDownloadPdf, openCheckInModal, openAmendModal, openCheckOutModal, openNoShowModal, openCancelModal, openExtendStayModal]);

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: 'New Booking', onClick: handleAddNew, tooltip: 'Create New Booking', variant: 'icon' });
    }
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' });
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply / Clear ---
  const handleFilterApply = useCallback(() => {
    setActiveChip(null);
    setQueryParams(prev => ({
      ...prev,
      propertyId: filterProperty || undefined,
      bookingStatusCodes: filterStatuses.length > 0 ? filterStatuses : undefined,
      bookingStatusCode: undefined, // multi-select supersedes single
      bookingSource: filterSource || undefined,
      agentId: filterAgent || undefined,
      corporateId: filterCorporate || undefined,
      otaId: filterOta || undefined,
      advanceStatus: filterAdvanceStatus || undefined,
      hasApprovalPending: filterApprovalPending || undefined,
      showCancelled: filterShowCancelled,
      dateFrom: filterDateFrom || undefined,
      dateTo: filterDateTo || undefined,
      dateType: filterDateType || 'checkin',
      settlementModes: filterSettlements.length > 0 ? (filterSettlements as SettlementMode[]) : undefined,
      settlementCheckInFrom: filterSettlements.length > 0 ? (filterSettlementFrom || todayKey()) : undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [filterProperty, filterStatuses, filterSource, filterAgent, filterCorporate, filterOta, filterAdvanceStatus, filterApprovalPending, filterShowCancelled, filterDateFrom, filterDateTo, filterDateType, filterSettlements, filterSettlementFrom]);

  const handleFilterClear = useCallback(() => {
    setActiveChip(null);
    // With a single property, clearing to "no property" would leave the strip,
    // Day View and Calendar blank with nothing to choose — so Clear returns to
    // the default rather than to empty.
    setFilterProperty(isSingleProperty ? defaultPropertyId : '');
    setFilterStatuses([]);
    setFilterSource('');
    setFilterAgent('');
    setFilterCorporate('');
    setFilterOta('');
    setFilterAdvanceStatus('');
    setFilterApprovalPending(false);
    setFilterShowCancelled(false);
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDateType('checkin');
    setFilterSettlements([]);
    setFilterSettlementFrom(todayKey());
    setQueryParams(prev => ({
      ...prev,
      propertyId: isSingleProperty ? defaultPropertyId : undefined,
      bookingStatusCode: undefined,
      bookingStatusCodes: undefined,
      bookingSource: undefined,
      agentId: undefined,
      corporateId: undefined,
      otaId: undefined,
      advanceStatus: undefined,
      hasApprovalPending: undefined,
      showCancelled: false,
      dateFrom: undefined,
      dateTo: undefined,
      dateType: 'checkin',
      settlementModes: undefined,
      settlementCheckInFrom: undefined,
      page: 1,
    }));
    // Deps are REQUIRED: the single-property default resolves after first
    // render, so an empty dep array would capture '' forever and Clear would
    // wipe the property on exactly the tenants this is meant to help.
  }, [isSingleProperty, defaultPropertyId]);

  // --- Quick-filter chips ---
  // Each chip pushes a preset to queryParams without touching the local filter state,
  // so the user can re-open the filter panel and refine on top of a chip selection.
  const applyChip = useCallback((chipId: string) => {
    setActiveChip(prev => prev === chipId ? null : chipId);
    if (activeChip === chipId) {
      // Toggle off → clear filters
      handleFilterClear();
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    let next: Partial<BookingSearchRequest> = {
      page: 1,
      bookingStatusCode: undefined,
      bookingStatusCodes: undefined,
      advanceStatus: undefined,
      hasApprovalPending: undefined,
      showCancelled: false,
      dateFrom: undefined,
      dateTo: undefined,
      dateType: 'checkin',
    };
    switch (chipId) {
      case 'pending-approval':
        next.bookingStatusCodes = ['PENDING_APPROVAL'];
        next.hasApprovalPending = true;
        break;
      case 'tentative-unpaid':
        next.bookingStatusCodes = ['TENTATIVE'];
        next.advanceStatus = 'unpaid';
        break;
      case 'overdue-advance':
        next.advanceStatus = 'overdue';
        break;
      case 'blocked-today':
        next.bookingStatusCodes = ['BLOCKED'];
        next.dateType = 'block_expiry';
        next.dateFrom = today;
        next.dateTo = today;
        break;
      case 'todays-arrivals':
        next.dateType = 'checkin';
        next.dateFrom = today;
        next.dateTo = today;
        break;
      case 'agent-follow-ups':
        // Source-scoped follow-ups: TENTATIVE + Unpaid for the currently-selected
        // Agent / Corporate / OTA. Chip is disabled when no party is selected.
        next.bookingStatusCodes = ['TENTATIVE'];
        next.advanceStatus = 'unpaid';
        next.agentId = filterAgent || undefined;
        next.corporateId = filterCorporate || undefined;
        next.otaId = filterOta || undefined;
        break;
    }
    setQueryParams(prev => ({ ...prev, ...next }));
  }, [activeChip, handleFilterClear, filterAgent, filterCorporate, filterOta]);

  /**
   * Strip cell activated: focus that day in the Day View tab and switch to it.
   * Deliberately does NOT filter the All Bookings grid - decision 13: never
   * apply a filter the agent cannot see. The date-scoped list lives in the tab.
   */
  const handleStripSelectDate = useCallback((isoDate: string) => {
    setStripDate(isoDate);
    setDayViewDate(isoDate);
    setActiveTab('day');
  }, []);

  /**
   * Day View -> All Bookings, carrying the date as a VISIBLE removable chip.
   * Labelled "Arrivals" because the grid filters on check-in date; the full
   * stay-overlap picture is the Day View itself.
   */
  const handleOpenFullListForDate = useCallback((isoDate: string) => {
    setActiveTab('all');
    setStripDate(isoDate);
    setActiveChip(null);
    setQueryParams((q) => ({
      ...q,
      page: 1,
      dateType: 'checkin',
      dateFrom: isoDate,
      dateTo: isoDate,
    }));
  }, []);

  /** Clears the date chip on the All Bookings grid. */
  const clearDateChip = useCallback(() => {
    setStripDate('');
    setQueryParams((q) => ({ ...q, page: 1, dateFrom: undefined, dateTo: undefined }));
  }, []);

  /**
   * The three views live in the ModuleHeader's view-mode toggle rather than a
   * tab strip: a tab row costs a whole band of vertical space on a screen whose
   * job is showing rows, and the header already has the control for exactly
   * this. Quick-filter chips belong to the All Bookings grid only — they filter
   * the query the other two views do not use.
   */
  const viewModeOptions: ViewModeOption[] = useMemo(() => [
    { id: 'all', icon: <IconSystem.list size={16} />, label: 'All Bookings',
      tooltip: 'All Bookings' },
    { id: 'day', icon: <IconSystem.grid size={16} />, label: 'Day View',
      tooltip: `Day View · ${dayViewDate ? parseDayKey(dayViewDate)
        .toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}` },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar',
      tooltip: 'Calendar View' },
  ], [dayViewDate]);

  // --- Applied-filter count for the filter button badge ---
  // Counts the structured filters currently in queryParams (excluding pagination,
  // search, sort, and the booking-default `dateType=checkin`/`showCancelled=false`).
  const appliedFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.propertyId)                 n++;
    if (queryParams.bookingStatusCodes && queryParams.bookingStatusCodes.length > 0) n++;
    else if (queryParams.bookingStatusCode)     n++;
    if (queryParams.bookingSource)              n++;
    if (queryParams.agentId)                    n++;
    if (queryParams.corporateId)                n++;
    if (queryParams.otaId)                      n++;
    if (queryParams.advanceStatus)              n++;
    if (queryParams.hasApprovalPending)         n++;
    if (queryParams.showCancelled)              n++;
    if (queryParams.dateFrom || queryParams.dateTo) n++;
    return n;
  }, [queryParams]);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Bookings"
        description="Manage reservations and booking records"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : undefined}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search booking #, guest, agent, OTA, corporate, ref...' }}
        viewModeConfig={{
          currentMode: activeTab,
          modes: viewModeOptions,
          onChange: (mode) => setActiveTab(mode as ViewMode),
        }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: appliedFilterCount }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={<ColumnSelector columns={columnDefinitions} visibleColumns={visibleColumns} onApply={handleApplyColumns} onReset={handleResetColumns} />}
        actions={actionButtons}
      />

      {/* Quick-filter chips — All Bookings only. They drive the grid query,
          which the Day View and Calendar do not use. */}
      {activeTab === 'all' && <div style={{
        padding: '6px 1.25rem 0',
        display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center',
      }}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, marginRight: '0.25rem' }}>
          QUICK VIEWS
        </span>
        {[
          { id: 'pending-approval', label: 'Pending Approval', icon: <IconSystem.alert size={12} /> },
          { id: 'tentative-unpaid', label: 'Tentative · Unpaid', icon: <IconSystem.clock size={12} /> },
          { id: 'overdue-advance',  label: 'Overdue Advance',   icon: <IconSystem.warning size={12} /> },
          { id: 'blocked-today',    label: 'Blocked Expiring Today', icon: <IconSystem.lock size={12} /> },
          { id: 'todays-arrivals',  label: "Today's Arrivals",  icon: <IconSystem.calendar size={12} /> },
          { id: 'agent-follow-ups', label: 'Agent Follow-Ups',  icon: <IconSystem.users size={12} /> },
        ].map(chip => {
          const active = activeChip === chip.id;
          // Agent Follow-Ups chip is contextual — only enabled when an Agent / Corporate / OTA is selected in the filter panel.
          const partyPicked = !!(filterAgent || filterCorporate || filterOta);
          const disabled = chip.id === 'agent-follow-ups' && !partyPicked;
          const tooltip = disabled
            ? 'Pick an Agent, Corporate, or OTA in the filter panel first to scope follow-ups to that party.'
            : undefined;
          return (
            <button
              key={chip.id}
              onClick={() => !disabled && applyChip(chip.id)}
              title={tooltip}
              disabled={disabled}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.25rem 0.625rem', borderRadius: '999px',
                fontSize: '0.7rem', fontWeight: 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                border: active ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                background: active ? 'var(--primary)' : 'var(--bg-primary)',
                color: active ? 'white' : 'var(--text-primary)',
                transition: 'all 0.15s ease',
              }}
            >
              {chip.icon}
              {chip.label}
            </button>
          );
        })}
        {activeChip && (
          <button
            onClick={() => { setActiveChip(null); handleFilterClear(); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.25rem 0.5rem', borderRadius: '999px',
              fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
              border: 'none', background: 'transparent', color: 'var(--text-muted)',
            }}
            title="Clear quick view"
          >
            <IconSystem.x size={12} /> Clear
          </button>
        )}

        {/* Visible, removable evidence of WHY the grid narrowed. */}
        {activeTab === 'all' && stripDate && queryParams.dateFrom === stripDate && (
          <button
            onClick={clearDateChip}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.25rem 0.625rem', borderRadius: '999px',
              fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
              border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff',
            }}
            title="Clear the date filter"
          >
            Arrivals: {new Date(stripDate + 'T00:00:00').toLocaleDateString('en-IN',
              { day: '2-digit', month: 'short' })}
            <IconSystem.x size={12} />
          </button>
        )}
      </div>}

      <OccupancyStrip
        propertyId={filterProperty}
        selectedDate={stripDate}
        onSelectDate={handleStripSelectDate}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container" style={{ display: activeTab === 'all' ? undefined : 'none' }}>
          {gridLoading && data.length === 0 ? (
            <GridLoadingSkeleton rows={10} columns={6} />
          ) : (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No bookings found"
              frozenLeftColumns={['select', 'bookingNumber']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}
        </div>

        {/* Day View. Kept MOUNTED (display:none) rather than unmounted so
            switching tabs does not discard its loaded sections and collapse
            state, and so the shared refetch keeps it current either way. */}
        <div
          className="data-grid-container"
          style={{ display: activeTab === 'day' ? undefined : 'none' }}
        >
          <DayViewPanel
            propertyId={filterProperty}
            date={dayViewDate}
            columns={tableColumns}
            rowActions={getRowActions}
            searchTerm={searchQuery}
            onEscapeSearch={() => setActiveTab('all')}
            onOpenFullList={() => handleOpenFullListForDate(dayViewDate)}
            refreshKey={dayRefreshKey}
          />
        </div>

        {/* Calendar View — the same day cell as the strip, month density. */}
        <div
          className="data-grid-container"
          style={{ display: activeTab === 'calendar' ? undefined : 'none' }}
        >
          <OccupancyCalendar
            propertyId={filterProperty}
            selectedDate={stripDate}
            onSelectDate={handleStripSelectDate}
            refreshKey={dayRefreshKey}
          />
        </div>

        {/* Filter panel */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button onClick={() => setFilterOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters">
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterProperty}
                onChange={(val: string | string[] | null) => setFilterProperty((val as string) || '')}
                placeholder="All Properties"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Booking Status"
                isMulti
                options={bookingStatusOptions}
                value={filterStatuses}
                onChange={(val: string | string[] | null) => setFilterStatuses(Array.isArray(val) ? val : [])}
                placeholder="All Statuses"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect label="Source" options={bookingSourceOptions} isDisabled={sourcesLoading} value={filterSource}
                onChange={(val: string | string[] | null) => setFilterSource((val as string) || '')}
                placeholder="All Sources" isClearable size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Agent"
                isAsync
                loadOptions={loadAgentOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterAgent}
                onChange={(val: string | string[] | null) => setFilterAgent((val as string) || '')}
                placeholder="All Agents"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Corporate"
                isAsync
                loadOptions={loadCorporateOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterCorporate}
                onChange={(val: string | string[] | null) => setFilterCorporate((val as string) || '')}
                placeholder="All Corporates"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="OTA"
                isAsync
                loadOptions={loadOtaOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterOta}
                onChange={(val: string | string[] | null) => setFilterOta((val as string) || '')}
                placeholder="All OTAs"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect label="Advance Status" options={advanceStatusOptions} value={filterAdvanceStatus}
                onChange={(val: string | string[] | null) => setFilterAdvanceStatus((val as string) || '')}
                placeholder="Any" isClearable size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Settlement (upcoming arrivals)"
                isMulti
                options={SETTLEMENT_MODE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                value={filterSettlements}
                onChange={(val: string | string[] | null) => setFilterSettlements(Array.isArray(val) ? val : [])}
                placeholder="Any"
                isClearable
                size="sm"
              />
            </div>
            {filterSettlements.length > 0 && (
              <div className="filter-section">
                <label className="form-label">Arrivals from</label>
                <input type="date" className="form-input" value={filterSettlementFrom}
                  onChange={(e) => setFilterSettlementFrom(e.target.value)}
                  title="Settlement filter: bookings checking in on or after this date"
                  style={{ fontSize: '0.8rem', height: '36px' }} />
              </div>
            )}
            <div className="filter-section">
              <SyutSelect label="Date Type" options={dateTypeOptions} value={filterDateType}
                onChange={(val: string | string[] | null) => setFilterDateType((val as string) || 'checkin')}
                placeholder="Check-In Date" isClearable={false} size="sm" />
            </div>
            <div className="filter-section">
              <label className="form-label">Date From</label>
              <input type="date" className="form-input" value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem', height: '36px' }} />
            </div>
            <div className="filter-section">
              <label className="form-label">Date To</label>
              <input type="date" className="form-input" value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{ fontSize: '0.8rem', height: '36px' }} />
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                <input type="checkbox" checked={filterApprovalPending} onChange={(e) => setFilterApprovalPending(e.target.checked)} />
                Has Approval Pending
              </label>
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                <input type="checkbox" checked={filterShowCancelled} onChange={(e) => setFilterShowCancelled(e.target.checked)} />
                Show Cancelled / No-Show
              </label>
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
            <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <BookingViewModal
        isOpen={viewModalOpen}
        booking={viewRecord}
        onClose={() => { setViewModalOpen(false); setViewRecord(null); }}
        onEdit={handleEdit}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Form Modal */}
      <BookingFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={formRecord}
        allowedActions={allowedActions}
        onSave={handleFormSaveSuccess}
        onClose={() => { setFormModalOpen(false); setFormRecord(null); }}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
      />

      {/* Advance Capture Modal — opens from the row action menu */}
      {advanceBooking && (
        <PaymentCaptureModal
          isOpen={advanceModalOpen}
          mode="create"
          collectionPoint="RESERVATIONS"
          bookingId={advanceBooking.bookingId}
          propertyId={advanceBooking.propertyId}
          bookingNumber={advanceBooking.bookingNumber}
          suggestedAmount={
            // Default = outstanding advance (advanceRequired − advanceReceived),
            // not the full net balance. Cashier can edit if guest pays more.
            Math.max(0, (advanceBooking.advanceRequired ?? 0) - (advanceBooking.advanceReceived ?? 0)) || undefined
          }
          onSave={handleAdvanceSaved}
          onClose={() => { setAdvanceModalOpen(false); setAdvanceBooking(null); }}
          tabId={tabId}
          moduleName={moduleName}
        />
      )}

      {/* Block (TENTATIVE-state action) — config-driven defaults + cap */}
      <BlockBookingModal
        isOpen={blockModalOpen}
        booking={blockTarget}
        onClose={() => { setBlockModalOpen(false); setBlockTarget(null); }}
        onBlocked={refetch}
      />

      {/* One-click Extend Block (BLOCKED-state action) */}
      <ExtendBlockModal
        isOpen={extendBlockOpen}
        booking={extendBlockTarget}
        onClose={() => { setExtendBlockOpen(false); setExtendBlockTarget(null); }}
        onExtended={refetch}
      />

      {/* PDF preview — shown before download. Cashier reviews then hits Download. */}
      <BookingPdfPreviewModal
        isOpen={!!pdfPreviewBooking}
        bookingId={pdfPreviewBooking?.bookingId ?? null}
        bookingNumber={pdfPreviewBooking?.bookingNumber}
        onClose={() => setPdfPreviewBooking(null)}
      />

      {/* TXN-STAT — Quick Check-In (QSM-CHKIN-001) */}
      <QuickCheckInModal
        isOpen={checkInModalOpen}
        booking={checkInTarget}
        onClose={() => { setCheckInModalOpen(false); setCheckInTarget(null); }}
        onCheckedIn={refetch}
        canChangeCategory={allowedActions.includes('room-category-change')}
        canChangeArrival={allowedActions.includes('arrival-changes')}
        canChangeSettlement={allowedActions.includes('change-settlement')}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreCheckIn={(b) => { setCheckInTarget(b); setCheckInModalOpen(true); }}
      />

      {/* Surgical source/channel correction — no money, no inventory. */}
      <ChangeSourceModal
        isOpen={!!sourceTarget}
        booking={sourceTarget}
        onClose={() => setSourceTarget(null)}
        onChanged={refetch}
      />

      <IssueInvoicesModal isOpen={!!invoiceTarget} bookingId={invoiceTarget}
        onClose={() => setInvoiceTarget(null)} onIssued={() => refetch()} />

      {/* In-house settlement change — who pays; never the amounts. */}
      <ChangeSettlementModal
        isOpen={!!settlementTarget}
        booking={settlementTarget}
        onClose={() => setSettlementTarget(null)}
        onChanged={() => refetch()}
      />


      {/* Post-check-in amend — same modal, amend mode, own session state. */}
      <QuickCheckInModal
        isOpen={!!amendTarget}
        booking={amendTarget}
        mode="amend"
        onClose={() => setAmendTarget(null)}
        onCheckedIn={refetch}
        canChangeCategory={false}
        canChangeArrival={allowedActions.includes('arrival-changes')}
        canChangeSettlement={allowedActions.includes('change-settlement')}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreCheckIn={(b) => setAmendTarget(b)}
      />

      {/* TXN-STAT — Quick Check-Out (QSM-CHKOT-001) */}
      <CheckOutRoomPickerModal
        isOpen={checkOutModalOpen}
        booking={checkOutTarget}
        onClose={() => { setCheckOutModalOpen(false); setCheckOutTarget(null); }}
        onCheckedOut={refetch}
      />

      {/* TXN-STAT — Mark No-Show (CONFIRMED → NO_SHOW) */}
      <MarkNoShowConfirmDialog
        isOpen={noShowModalOpen}
        booking={noShowTarget}
        onClose={() => { setNoShowModalOpen(false); setNoShowTarget(null); }}
        onMarked={refetch}
      />

      {/* Cancel Booking (TENTATIVE/BLOCKED/PENDING_APPROVAL/CONFIRMED → CANCELLED) */}
      <CancelBookingConfirmDialog
        isOpen={cancelModalOpen}
        booking={cancelTarget}
        onClose={() => { setCancelModalOpen(false); setCancelTarget(null); }}
        onCancelled={refetch}
      />

      {/* Extend Stay (CHECKED_IN only — pushes check-out date forward) */}
      <ExtendStayModal
        isOpen={extendStayModalOpen}
        booking={extendStayTarget}
        onClose={() => { setExtendStayModalOpen(false); setExtendStayTarget(null); }}
        onExtended={refetch}
      />

      {/* Confirm Block (manual TENTATIVE→CONFIRMED for BLOCKED rows) */}
      <ConfirmDialog
        open={confirmBlockedOpen}
        title="Confirm Block"
        message={`Confirm booking "${confirmBlockedTarget?.bookingNumber ?? ''}"? This will clear the auto-cancel deadline and transition the booking to CONFIRMED, bypassing the advance-paid check.`}
        confirmLabel="Confirm Booking"
        cancelLabel="Cancel"
        variant="default"
        loading={confirmBlockedSaving}
        loadingMessage="Confirming..."
        onConfirm={handleConfirmBlockedExecute}
        onCancel={() => setConfirmBlockedOpen(false)}
      />

      {/* Request Due-Date Extension inline modal */}
      {extensionModalOpen && extensionTarget && (
        <>
          <div className="modal-backdrop" onClick={() => !extensionSaving && setExtensionModalOpen(false)} />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Request Due-Date Extension</h2>
              <button className="modal-close" onClick={() => setExtensionModalOpen(false)} disabled={extensionSaving}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                Booking <strong>{extensionTarget.bookingNumber}</strong> — request a new advance due date. Will be sent for manager approval.
              </p>
              <div className="form-group">
                <label className="form-label">Current Due Date</label>
                <input type="text" className="form-input" value={formatDate(extensionTarget.advanceDueDate)} disabled />
              </div>
              <div className="form-group">
                <label className="form-label">Proposed Due Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-input"
                  value={extensionDate}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  max={extensionTarget.checkInDate?.slice(0, 10)}
                  onChange={(e) => setExtensionDate(e.target.value)}
                  disabled={extensionSaving}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Justification <span className="text-danger">*</span></label>
                <textarea
                  className="form-input form-textarea"
                  rows={3}
                  value={extensionRemarks}
                  onChange={(e) => setExtensionRemarks(e.target.value)}
                  placeholder="Reason for the extension request..."
                  disabled={extensionSaving}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setExtensionModalOpen(false)} disabled={extensionSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleExtensionConfirm} disabled={extensionSaving || !extensionDate || !extensionRemarks.trim()}>
                {extensionSaving ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default BookingListScreen;
